import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, TextField, Typography } from '@mui/material';
import { IReduxState } from 'utils/models/redux-states';
import ApiService from 'utils/services/ApiService';
import Text from '../text';
import Groups from './Groups';
import Button from '../button';
import { IGroups } from 'utils/models/groups';
import ConfirmationButtons from '../button/ConfirmationButtons';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { DragDropContext as DragDropContextBase, Droppable as DroppableBase } from 'react-beautiful-dnd';
import { setGroupsInformation, addGroup, updateGroup, addSiteToGroup } from 'utils/redux/reducers/groupsReducer';
import { setUpdateReduxData } from 'utils/redux/reducers/updateDataReducer';
import { getTestResults, saveFailedRequest, saveTestResults } from 'utils/services/idbService';
import useCheckUserIsOnline from 'hooks/useCheckUserOnline';
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import type { DroppableProps,  DragDropContextProps } from 'react-beautiful-dnd';
import { saveSyncCache } from 'utils/services/idbService';
import { SiteAllVO } from 'utils/models/sync-catch';
import CONSTANTS from '../../utils/constants/UIConstants';
import { addEventLog } from 'utils/functions/eventLogFunctions';

const DragDropContext = DragDropContextBase as React.ComponentClass<DragDropContextProps>;
const Droppable = DroppableBase as React.ComponentClass<DroppableProps>;

const SitesAndGroups = () => {
	const dispatch = useDispatch(); // Get the dispatch function from the useDispatch hook
	const sendPopUpMessage = usePopupMessage();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const reduxGroupsState: IGroups[] | undefined = useSelector((state: IReduxState) => state?.groups?.groups);
	const userData = reduxState?.userInformation;
	const viewedUserState = reduxState?.viewedUserInfo ?? 0;
	const [groups, setGroups] = useState<IGroups[]>([]);
	const [addNewGroup, setAddNewGroup] = useState(false);
	const [showDeleteGroup, setShowDeleteGroup] = useState(0);
	const [newGroup, setNewGroup] = useState<any>();
	const [confirmationLoading, setConfirmationLoading] = useState(false);
	const [newCachedSite, setNewCachedSite] = useState({
		id: 0,
		name: '',
		sortOrder: 9999,
		sharedUserVOs: [
			{
				id: userData?.id,
				email: userData?.username,
				owner: true,
			},
		],
		tableColumnSettingsAllVOs: [
			{
				colName: 'Operator',
				colType: 'Sample',
				visible: false,
			},
			{
				colName: 'Notes',
				colType: 'Sample',
				visible: true,
			},
			{
				colName: 'Location',
				colType: 'Sample',
				visible: true,
			},
			{
				colName: 'Test Kit',
				colType: 'Sample',
				visible: true,
			},
		],
		sampleLocationAllVOs: [],
	});
	const [newSite, setNewSite] = useState({
		name: '',
		groupId: 0,
		timeZone: 'America/Halifax',
		applicationVO: {
			id: 2,
			name: 'Activated Sludge',
			industry: 'Waste Water',
			status: 'Active',
			waterSources: [],
			locationTypes: [],
		},
		isActive: true,
		creationDate: new Date().toISOString(),
		modificationDate: new Date().toISOString(),
		creatorId: userData?.email,
		editorId: userData?.email,
		status: 'Active',
		userVOs: [
			{
				email: userData?.email,
				firstName: userData?.firstName,
				id: userData?.id,
				lastName: userData?.lastName,
				parentCompany: userData?.parentCompany,
				status: 'Active',
				username: userData?.username,
				permissions: [],
				userPermissionVOs: [
					{
						role: 'USER',
					},
				],
			},
		],
		coOwnerVOs: [
			{
				email: userData?.email,
				firstName: userData?.firstName,
				id: userData?.id,
				lastName: userData?.lastName,
				parentCompany: userData?.parentCompany,
				status: 'Active',
				username: userData?.username,
			},
		],
		dashBoardSiteStatisticsVO: {
			lasmodificationDate: new Date().toISOString(),
			lastActiveUser: ' ',
			parentCompany: ' ',
			totalAnalyticsConfigured: 0,
			totalatpDataPoints: 0,
			totalAtpDataSets: 0,
			totalReportsConfigured: 0,
			totalSamplePoints: 0,
			totalSupplemantalDataPoints: 0,
			totalSupplemantalDataSets: 0,
		},
		addressVO: {
			countryName: ' ',
			provinceName: ' ',
			city: ' ',
			streetAddress1: ' ',
		},
	});
	const [errors, setErrors] = useState({
		siteName: '',
		groupName: '',
	});
	// const { isOnline, Alert } = useCheckUserIsOnline();
  const { isOnline } = useCheckUserIsOnline();
	const validateAddGroup = () => {
		if (!newGroup?.name) {
			setErrors({
				...errors,
				groupName: 'Please enter a group name',
			});
			return 'No Group name provided.';
		}
		return false;
	};

	const resetErrors = () => {
		setErrors({
			siteName: '',
			groupName: '',
		});
	};

	const handleAddNewGroup = async () => {
		const errorMsg = validateAddGroup();
		if (errorMsg) {
			addEventLog("Create Group Failed", "Group Management", undefined, undefined, `Group Name: ${newGroup.name}, Creator: ${userData?.email}, Error: ${errorMsg}`);
			return;
		}
		setConfirmationLoading(true);
		
		try {
			if (!isOnline) {
				const newGroupData = {
					id: new Date().getTime(), // Temporary ID for offline
					name: newGroup.name,
					sites: [],
					userId: userData?.id ?? 0
				};
				
				await saveSyncCache({
					id: newGroupData.id,
					name: newGroupData.name,
					action: 'CREATE'
				}, "groupSyncCache");
				const groupsCache = await getTestResults(
					`groupsInfo`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				groupsCache.push(newGroupData);
				await saveTestResults(
					`groupsInfo`,
					groupsCache,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				// Update Redux state
				dispatch(addGroup(newGroupData));
				setAddNewGroup(false);
				return;
			}

			const result = await ApiService.createAGroup(newGroup, userData?.email);
			if (result.status === 200) {
				// Update Redux state with server response
				// dispatch(addGroup(result.data));
				setAddNewGroup(false);
				sendPopUpMessage('Success', 'Group added successfully.');
			}
		} catch (error) {
			addEventLog("Create Group Failed", "Group Management", undefined, undefined, `Group Name: ${newGroup.name}, Creator: ${userData?.email}, Error: ${error}`);
			console.log(error);
			// setAddNewGroup(false);
			// sendPopUpMessage(
			// 	'Error',
			// 	'Changes not saved. Please refresh your browser and try again.'
			// );
		} finally {
			setConfirmationLoading(false);
		}
	};

	const handleAddNewGroupCancel = () => {
		setAddNewGroup(false);
		setErrors({
			siteName: '',
			groupName: '',
		});
		setNewSite({
			...newSite,
			name: '',
		});
		setNewGroup({ name: '' });
	};

	const handleAddNewSite = async (updatedSite?: any) => {
		try {
			if (!isOnline) {
				const tempId = new Date().getTime();
				const site = updatedSite || newSite;

				// Create site data objects
				const newSiteData = {
					coOwnerVOs: site?.coOwnerVOs || [],
					creationDate: new Date().toISOString(),
					id: tempId,
					isActive: true,
					modificationDate: new Date().toISOString(),
					name: updatedSite?.name || newSite.name,
					siteIndustryVOs: site?.siteIndustryVOs || [],
					userVOs: site?.userVOs || [],
					sortOrder: 999,
				};

				// First update the groupsInfo cache
				const groupsCache = await getTestResults(
					`groupsInfo`,
					localStorage.getItem('currentUser') ?? 'unknown'
				) || [];

				const updatedGroupsCache = groupsCache.map((group: any) => {
					if (group.id === site?.groupId) {
						return {
							...group,
							sites: [...(group.sites || []), newSiteData]
						};
					}
					return group;
				});

				// Save updated groups cache
				await saveTestResults(
					`groupsInfo`,
					updatedGroupsCache,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				// Update Redux state
				dispatch(addSiteToGroup({ 
					groupId: site?.groupId, 
					site: newSiteData 
				}));

				// Update allSites cache
				const allSitesCache = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				) || [];
				const defaultColumns:any[] = [
					{
						"colName": "Operator",
						"colType": "Sample",
						"desktop": false,
						"mobile": false,
						"siteId": tempId,
						"userId": userData?.id,
						"visible": false
					},
					{
						"colName": "Notes",
						"colType": "Sample",
						"desktop": true,
						"mobile": true,
						"siteId": tempId,
						"userId": userData?.id,
						"visible": true
					},
					{
						"colName": "Location",
						"colType": "Sample",
						"desktop": true,
						"mobile": true,
						"siteId": tempId,
						"userId": userData?.id,
						"visible": true
					},
					{
						"colName": "Test Kit",
						"colType": "Sample",
						"desktop": true,
						"mobile": true,
						"siteId": tempId,
						"userId": userData?.id,
						"visible": true
					}
				]
				const catchSite = {
					id: tempId,
					name: updatedSite?.name || newSite.name,
					sampleLocationAllVOs: [],
					sharedUserVOs: [{
						email: userData?.email,
						id: userData?.id,
						owner: true,
						name: `${userData?.firstName} ${userData?.lastName}`,
					}],
					tableColumnSettingsAllVOs: defaultColumns,
					sortOrder: 999,
					timestamp: new Date().getTime(),
				};

				allSitesCache.push(catchSite);
				await saveTestResults(
					`allSites`,
					allSitesCache,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				dispatch(setAllSitesData(allSitesCache));

				// Save to sync cache for when online
				await saveSyncCache({
					id: tempId,
					groupFK: site?.groupId,
					name: site?.name,
					sortOrder: 999,
					sharedUserVOs: site?.userVOs || [],
					tableColumnSettingsAllVOs: defaultColumns,
					action: 'CREATE'
				}, "siteSyncCache");

				sendPopUpMessage('Success', 'Site created successfully. Changes will sync when online.');
				setAddNewGroup(false);
				return;
			}

			// Online mode
			const result = await ApiService.createNewSite(
				updatedSite || newSite,
				userData?.organizationVO?.id ?? 1,
				newSite?.creatorId ?? ''
			);

			if (result.status !== 200) {
				throw Error('Error connecting with the server.');
			}
			if (!updatedSite && userData) {
				let cachedData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				const newCache = {...(newCachedSite||{})};
				newCache.id = result.data;
				newCache.name = newSite.name;
				cachedData.push(newCache);
				setNewCachedSite(()=>newCache);

				
				const sortedData = cachedData.sort((a: SiteData, b:SiteData) => a.name.localeCompare(b.name));
				dispatch(setAllSitesData(sortedData))
				await saveTestResults(
					`allSites`,
					cachedData,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				sendPopUpMessage('Success', 'Site added successfully.');
			} else if (!userData) {
				sendPopUpMessage(
					'Success',
					'BUG: REDUX CACHE USER MISSING. PLEASE REFRESH WHILE ONLINE.'
				);
			}
		} catch (error) {
			console.error('Create site error:', error);
			sendPopUpMessage(
				'Error',
				'Changes not saved. Please try again.'
			);
		} finally {
			setConfirmationLoading(false);
		}
	};

	const handleGroupName = (value: string) => {
		if (errors.siteName !== '' || errors.groupName !== '') resetErrors();
		setNewGroup({ name: value });
	};

	const addNewGroupClick = () => {
		resetErrors();
		setNewGroup({ name: '' });
		setNewSite({
			...newSite,
			name: '',
		});
		setAddNewGroup(true);
	};
	const reduxGroupsMemo = useMemo(() => reduxGroupsState || [], [reduxGroupsState]);

	useEffect(() => {
		if (reduxGroupsState) {
			if (groups.length === 0 || JSON.stringify(groups) !== JSON.stringify(reduxGroupsState)) {
				setGroups(reduxGroupsState);
			}
		}
	}, [reduxGroupsState]);

	const handleDragDrop = async (results: any) => {
		const { source, destination, type } = results;
		if (!destination) return;
		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		) {
			return;
		}

		if (type === 'group') {
			const reorderedGroups = Array.from(groups);
			const [removedGroup] = reorderedGroups.splice(source.index, 1);
			reorderedGroups.splice(destination.index, 0, removedGroup);
			setGroups(reorderedGroups);
			processGroups(reorderedGroups);
			return;
		}

		const sourceGroupId = Number(source.droppableId);
		const destinationGroupId = Number(destination.droppableId);

		const newGroups = groups.map((group) => ({
			...group,
			sites: [...(group.sites || [])],
		}));

		const sourceGroupIndex = newGroups.findIndex(
			(group) => group.id === sourceGroupId
		);
		const destinationGroupIndex = newGroups.findIndex(
			(group) => group.id === destinationGroupId
		);

		if (sourceGroupIndex === -1 || destinationGroupIndex === -1) {
			console.error('Group index not found', {
				sourceGroupId,
				destinationGroupId,
			});
			return;
		}

		const sourceSites = newGroups[sourceGroupIndex].sites;
		const destinationSites = newGroups[destinationGroupIndex].sites;

		const [movedSite] = sourceSites.splice(source.index, 1);
		destinationSites.splice(destination.index, 0, movedSite);

		// Update site sort orders within their respective groups
		newGroups[sourceGroupIndex].sites = sourceSites.map((site, index) => ({
			...site,
			sortOrder: index + 1
		}));

		if (sourceGroupId !== destinationGroupId) {
			newGroups[destinationGroupIndex].sites = destinationSites.map((site, index) => ({
				...site,
				sortOrder: index + 1
			}));
		}

		setGroups(newGroups);

		if (!isOnline) {
			try {
				// Update groupsInfo cache
				await saveTestResults(
					`groupsInfo`,
					newGroups,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				dispatch(setGroupsInformation(newGroups));

				// Update allSites cache with new sort orders
				let allSitesCache = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				let sortOrder = 1;
				const siteIdToSortOrderMap = new Map();

				// Build sort order map from reordered groups
				newGroups.forEach(group => {
					if (group.sites?.length) {
						group.sites.forEach(site => {
							siteIdToSortOrderMap.set(site.id, sortOrder++);
						});
					}
				});

				// Update sort orders in allSites cache
				const updatedAllSites = allSitesCache.map((site: any) => ({
					...site,
					sortOrder: siteIdToSortOrderMap.get(site.id) || site.sortOrder
				}));

				await saveTestResults(
					`allSites`,
					updatedAllSites,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				// Update Redux state with new sort orders
				dispatch(setAllSitesData(updatedAllSites));

				// Save to failed requests for sync when online
				const requestBody = Array.from(siteIdToSortOrderMap.entries()).map(([siteId, sortOrder]) => ({
					siteId,
					sortOrder,
					groupId: newGroups.find(g => g.sites?.some(s => s.id === siteId))?.id
				}));

				await saveFailedRequest({
					url: process.env.REACT_APP_BACKEND_URI + '/cwds/services/organizationservice/setsiteorders',
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(requestBody)
				});

				sendPopUpMessage('Success', 'Site order updated. Changes will sync when online.');
			} catch (error) {
				console.error('Failed to update site order in offline mode:', error);
				sendPopUpMessage('Error', 'Failed to update site order. Please try again.');
			}
		} else {
			processGroups(newGroups);
		}
	};

	const getGroups = async () => {
		const userData = reduxState?.userInformation;
		const userId = userData?.id;
		if (userId && navigator.onLine) {
			try {
				let userIdToSend = userId;
				if (viewedUserState !== 0) {
					userIdToSend = viewedUserState;
				}
				const result = await ApiService.getUserGroups(userIdToSend);
				if (result.status !== 200)
					throw Error('Error connecting with the server.');
				dispatch(setGroupsInformation(groups));
				await saveTestResults(
					`groupsInfo`,
					groups,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				sendPopUpMessage('Success', 'Site updated successfully.');
			} catch (error) {
				console.log(error);
			}
		}
		dispatch(setUpdateReduxData(true));
	};

	const processGroups = async (groups: any[]) => {
		let sortOrder = 1;
		const requestBody: { siteId: any; sortOrder: number; groupId: any }[] =
			[];
		const siteIdToSortOrderMap = new Map();

		if (!groups) {
			return;
		}

		groups.forEach((group: { sites: any[]; id: any }) => {
			if (group.sites?.length) {
				group.sites.forEach((site: { id: any }) => {
					siteIdToSortOrderMap.set(site.id, sortOrder);
					requestBody.push({
						siteId: site.id,
						sortOrder: sortOrder++,
						groupId: group.id,
					});
				});
			}
		});

		const filteredRequestBody = requestBody.filter(
			(item) => item.groupId !== undefined
		);

		try {
			const response = await ApiService.postSortOrderSitesAndGroups(
				filteredRequestBody
			);

			let cachedData = await getTestResults(
				`allSites`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);

			//update cache with one loop
			for (let site of cachedData) {
				if (siteIdToSortOrderMap.has(site.id)) {
					site.sortOrder = siteIdToSortOrderMap.get(site.id);
				}
			}

			await saveTestResults(
				`allSites`,
				cachedData,
				localStorage.getItem('currentUser') ?? 'unknown'
			);

			await getGroups();
		} catch (error) {
			console.error('Failed to update site and group order', error);
			// Optionally handle the error further, e.g., send a popup message
		}
	};

	return (
		<>
			<div
				id="sites-groups-header"
				className="!bg-white sticky pt-[1px] pb-[10px]"
			>
				{/* {Alert} */}
				{/* Sticky Container for fixed content */}
				<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
					Manage Sites & Groups
				</Text>
				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] ">
					Add new sites, remove sites, or adjust display groupings.{' '}
					<br></br>
					Removing sites does not delete their data.{' '}
					<a
						className="!text-[#0092C8]"
						href="https://my.luminultra.com/hc/en-us/requests/new"
						target="_blank"
					>
						Contact support
					</a>{' '}
					to delete data permanently.
				</Text>
			</div>
			<div className="flex justify-center">
				<div className="flex-1 py-4 text-left">
					<Text addStyle="!text-[16px] !font-bold !font-inter">
						Group
					</Text>
					<Text addStyle="!text-[#2C2C2C] !font-jost !text-[14px] !mt-[12px] !mr-[60px]">
						<em>
							Used to display multiple related sites on one page.{' '}
							<br></br>
							Example: "East Coast Facilities"
						</em>
					</Text>
				</div>
				<div className="flex-1 py-4 text-left">
					<Text addStyle="!text-[16px] !font-bold !font-inter">
						Sites
					</Text>
					<Text addStyle="!text-[#2C2C2C] !font-jost !text-[14px] !mt-[12px] !mr-[60px]">
						<em>
							Physical places where samples are taken from.{' '}
							<br></br>Example: "New York Facility"
						</em>
					</Text>
				</div>
			</div>
			<Divider
				variant="middle"
				style={{
					margin: '0px 0 16px 0',
					backgroundColor: CONSTANTS.APP_BACKGROUND_COLOR,
				}}
				className="mt-[40px]"
			/>

			<DragDropContext onDragEnd={handleDragDrop}>
				<Droppable droppableId="ROOT" type="group">
					{(provided: any) => (
						<div
							{...provided.droppableProps}
							ref={provided.innerRef}
						>
							{groups?.map((group, idx) => {
								return (
									<div key={`tests-${idx}`}>
										<Groups
											index={idx}
											userData={userData}
											group={group}
											allGroups={groups}
											showDeleteGroup={showDeleteGroup}
											setShowDeleteGroup={
												setShowDeleteGroup
											}
											sendPopUpMessage={sendPopUpMessage}
											newSite={newSite}
											setNewSite={setNewSite}
											handleAddNewSite={handleAddNewSite}
											viewedUserState={viewedUserState}
										/>

										<Divider
											variant="middle"
											style={{
												margin: '16px 0',
												backgroundColor: CONSTANTS.APP_BACKGROUND_COLOR,
											}}
										/>
									</div>
								);
							})}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>

			{addNewGroup && (
				<>
					<div className="flex flex-row gap-[20px]">
						<div className="py-2 mb-2 new-group">
							<div className="flex items-center justify-between">
								<TextField
									size="small"
									value={newGroup?.name}
									style={{
										width: '298px',
										marginTop: 10,
										marginRight: 8,
									}}
									onChange={(e) =>
										handleGroupName(e.target.value)
									}
									error={errors.groupName !== ''}
									helperText={errors.groupName}
									label="Group Name"
								/>

								<div className="pt-[10px]">
									<ConfirmationButtons
										handleConfirm={handleAddNewGroup}
										handleCancel={handleAddNewGroupCancel}
										disabled={
											 confirmationLoading
										}
									/>
								</div>
							</div>
						</div>
						{}
					</div>

					<Divider
						variant="middle"
						style={{
							margin: '40px 0 16px 0',
							backgroundColor: CONSTANTS.APP_BACKGROUND_COLOR,
						}}
						className="mt-[40px]"
					/>
				</>
			)}
			
			<Button
				addStyle={`!w-[170px] !h-[48px] !font-bold ${
					viewedUserState === 0 ? '!text-[#0092C8]' : '!text-[#e6e6e6]'
				} !text-[14px] !font-bold add-new-group`}
				onClick={addNewGroupClick}
				disabled={viewedUserState !== 0}
			>
				+ Add New Group
			</Button>
	
		</>
	);
};

export default SitesAndGroups;




